<template>
  <div class="rcp-backdrop" @click.self="$emit('close')">
    <div class="rcp-panel">
      <div class="rcp-header">
        <span class="rcp-title">Enviar recordatorio</span>
        <button class="rcp-close" @click="$emit('close')"><span class="mdi mdi-close"></span></button>
      </div>

      <div class="rcp-body">
        <div class="rcp-type-pills">
          <button v-for="opt in TYPE_OPTIONS" :key="opt.value" class="rcp-pill" :class="{ 'rcp-pill--active': type === opt.value }" @click="onTypeChange(opt.value)">
            {{ opt.label }}
          </button>
          <span v-if="modified" class="rcp-modified-tag">Modificado</span>
        </div>

        <label class="rcp-label">Asunto</label>
        <input v-model="subject" class="rcp-input" @input="modified = true" />

        <label class="rcp-label">Destinatario</label>
        <input v-model="recipient" class="rcp-input" placeholder="correo@inquilino.com" />

        <label class="rcp-cc-toggle">
          <input type="checkbox" v-model="showCc" /> Enviar copia a
        </label>
        <input v-if="showCc" v-model="ccEmail" class="rcp-input" placeholder="correo@ejemplo.com" />

        <label class="rcp-toggle-row">
          <input type="checkbox" v-model="includeStatement" /> Adjuntar estado de cuenta
        </label>

        <label class="rcp-label">Cuerpo del mensaje</label>
        <div v-if="!editingBody" class="rcp-body-preview" @click="editingBody = true">{{ body }}</div>
        <textarea v-else v-model="body" class="rcp-body-textarea" rows="8" @input="modified = true"></textarea>
        <button class="rcp-link-btn" @click="editingBody = !editingBody">{{ editingBody ? 'Ver vista previa' : 'Editar' }}</button>
      </div>

      <div class="rcp-footer">
        <div v-if="errorMsg" class="rcp-notice" role="alert">{{ errorMsg }}</div>
        <button class="rcp-send-btn" :disabled="sending" @click="onSend">{{ sending ? 'Enviando...' : 'Enviar recordatorio' }}</button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { getReminderPreview, sendFullReminder } from '@/services/charges'

const props = defineProps({
  contractId: { type: String, required: true },
  parkId: { type: String, required: true },
  pendingAmount: { type: Number, default: 0 },
  dueDate: { type: String, default: null },
  overdueDays: { type: Number, default: 0 },
  defaultRecipient: { type: String, default: '' },
})

const emit = defineEmits(['close', 'sent'])

const TYPE_OPTIONS = [
  { value: 'PREVENTIVE', label: 'Preventivo' },
  { value: 'COLLECTION', label: 'De cobro' },
  { value: 'FORMAL', label: 'Notificación formal' },
]

const type = ref(props.overdueDays > 45 ? 'FORMAL' : props.overdueDays > 0 ? 'COLLECTION' : 'PREVENTIVE')
const subject = ref('')
const body = ref('')
const recipient = ref(props.defaultRecipient)
const showCc = ref(false)
const ccEmail = ref('')
const includeStatement = ref(true)
const editingBody = ref(false)
const modified = ref(false)
const sending = ref(false)
const errorMsg = ref('')
const folioNumber = ref(null)

async function loadContent() {
  const content = await getReminderPreview(props.contractId, props.parkId, type.value, props.pendingAmount, props.dueDate, props.overdueDays)
  subject.value = content.subject
  body.value = content.body
  folioNumber.value = content.folioNumber || null
  modified.value = false
}
onMounted(loadContent)

function onTypeChange(v) {
  type.value = v
  loadContent()
}

async function onSend() {
  sending.value = true
  errorMsg.value = ''
  try {
    await sendFullReminder({
      contractId: props.contractId, parkId: props.parkId, type: type.value, subject: subject.value, body: body.value,
      recipient: recipient.value, ccEmail: showCc.value ? ccEmail.value : null, includeStatement: includeStatement.value, folioNumber: folioNumber.value,
    })
    emit('sent')
  } catch (err) {
    errorMsg.value = err.message || 'No se pudo enviar el recordatorio'
  } finally {
    sending.value = false
  }
}
</script>

<style scoped>
.rcp-backdrop { position: fixed; inset: 0; background: rgba(15, 23, 42, 0.3); display: flex; align-items: center; justify-content: center; z-index: 2000; }
.rcp-panel { width: 320px; max-height: 85vh; background: #ffffff; border-radius: 10px; box-shadow: 0 20px 60px rgba(0,0,0,0.25); display: flex; flex-direction: column; overflow: hidden; }
.rcp-header { display: flex; align-items: center; padding: 0.75rem 1rem; border-bottom: 1px solid #f1f5f9; }
.rcp-title { font-family: var(--font-display); font-weight: 700; font-size: 14px; color: #1e3a5f; }
.rcp-close { margin-left: auto; background: none; border: none; color: #94a3b8; cursor: pointer; display: flex; }
.rcp-body { flex: 1; min-height: 0; overflow-y: auto; padding: 0.75rem 1rem; display: flex; flex-direction: column; gap: 0.4rem; }
.rcp-body::-webkit-scrollbar { width: 6px; }
.rcp-body::-webkit-scrollbar-thumb { background: var(--border-active); border-radius: 3px; }
.rcp-body::-webkit-scrollbar-track { background: transparent; }

.rcp-type-pills { display: flex; flex-wrap: wrap; gap: 0.3rem; align-items: center; margin-bottom: 0.4rem; }
.rcp-pill { background: #ffffff; border: 1px solid #e2e8f0; border-radius: 20px; padding: 0.25rem 0.55rem; font-family: var(--font-body); font-size: 11px; color: #64748b; cursor: pointer; }
.rcp-pill--active { background: #eff6ff; border-color: #3b82f6; color: #3b82f6; font-weight: 500; }
.rcp-modified-tag { font-size: 9px; color: #d97706; background: #fef3c7; padding: 0.1rem 0.3rem; border-radius: 4px; }

.rcp-label { font-family: var(--font-body); font-size: 10px; text-transform: uppercase; color: #94a3b8; margin-top: 0.3rem; }
.rcp-input { font-family: var(--font-body); font-size: 12px; border: 1px solid #e2e8f0; border-radius: 4px; padding: 0.4rem 0.5rem; background: #f8f9fc; }
.rcp-cc-toggle, .rcp-toggle-row { display: flex; align-items: center; gap: 0.4rem; font-family: var(--font-body); font-size: 11px; color: #1e3a5f; margin-top: 0.2rem; }

.rcp-body-preview { max-height: 120px; overflow: hidden; font-family: var(--font-body); font-size: 11px; color: #64748b; background: #f8f9fc; border: 1px solid #e2e8f0; border-radius: 6px; padding: 0.5rem; white-space: pre-wrap; cursor: pointer; }
.rcp-body-textarea { font-family: var(--font-body); font-size: 11px; border: 1px solid #e2e8f0; border-radius: 6px; padding: 0.5rem; resize: vertical; }
.rcp-link-btn { background: none; border: none; color: #3b82f6; font-family: var(--font-body); font-size: 11px; cursor: pointer; padding: 0; align-self: flex-start; }

.rcp-footer { padding: 0.75rem 1rem; border-top: 1px solid #f1f5f9; }
.rcp-send-btn { width: 100%; background: #3b82f6; color: #ffffff; border: none; border-radius: 6px; height: 36px; font-family: var(--font-body); font-size: 13px; font-weight: 500; cursor: pointer; }
.rcp-send-btn:disabled { opacity: 0.6; cursor: default; }
.rcp-notice { font-family: var(--font-body); font-size: 12px; line-height: 1.4; border-radius: 6px; padding: 0.5rem 0.65rem; margin-bottom: 0.6rem; background: #fef2f2; color: #b91c1c; border: 1px solid #fecaca; }
</style>